import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import { paymentRequest } from "@/api/user/home";
import AlertDialog from "@/shared/components/kit/modal";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid } from "@mui/material";
import { toast } from "react-toastify";
import BoxKit from "@/shared/components/kit/box";
import { numberSeparator } from "@/hooks/useThousandsSeparator";

type OnlinePaymentProps = {
  setModalPaymentCash: (value: boolean) => void;
  modalPaymentCash: boolean;
  installmentId: number;
  loanId: number;
  selectedCard: string;
  onPaymentSubmit: (
    installmentId: number,
    loanId: number,
    amount: number,
    gateway: string
  ) => Promise<void>;
  paymentAmount: number;
};

/**
 * OnlinePayment
 *
 * نمایش مودال پرداخت آنلاین و هدایت کاربر به درگاه بانکی سپهــر صــادرات.
 * این کامپوننت امکان انتخاب درگاه پرداخت، ارسال درخواست به سرور،
 * مدیریت وضعیت پرداخت، و ریدایرکت کاربر به صفحه‌ی پرداخت را فراهم می‌کند.
 *
 * @param setModalPaymentCash کنترل وضعیت باز/بسته بودن مودال
 * @param modalPaymentCash وضعیت فعال بودن مودال
 * @param installmentId شناسه قسط انتخاب‌شده
 * @param loanId شناسه وام مرتبط با قسط
 * @param selectedCard کارت انتخاب‌شده (در صورت وجود)
 * @param onPaymentSubmit تابع ارسال درخواست پرداخت
 * @param paymentAmount مبلغ پرداختی این تراکنش
 *
 * @returns JSX.Element
 */

const OnlinePayment = ({setModalPaymentCash,modalPaymentCash,installmentId,loanId,selectedCard,onPaymentSubmit,paymentAmount,}: OnlinePaymentProps) => {
  const chosePayment = [{ id: 1, title: "fanava", imgUrl: "/fanavar.png" }];
  const [selectedGateway, setSelectedGateway] = useState<string | null>(null);

  const { mutate: fetchRequestRegister, isPending } = useMutation({
    mutationFn: paymentRequest,
    onSuccess: (data: any) => {
      setModalPaymentCash(false);

      if (data?.data?.url) {
        window.location.href = data.data.url;
      } else {
        toast.error("لینک پرداخت ایجاد نشد. لطفا دوباره تلاش کنید.");
      }
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(
        error?.response?.data?.message ||
          "خطا در پرداخت. لطفا دوباره تلاش کنید."
      );
    },
  });

  const handleAgree = () => {
    if (!selectedGateway) {
      toast.error("لطفا یک درگاه پرداخت انتخاب کنید");
      return;
    }

    const paymentData = {
      installmentId,
      loanId,
      amount: Number(paymentAmount),
      gateway: selectedGateway,
    };

    fetchRequestRegister(paymentData);
  };

  return (
    <AlertDialog
      onClose={() => setModalPaymentCash(false)}
      open={modalPaymentCash}
      onDisagree={() => setModalPaymentCash(false)}
      onAgree={handleAgree}
      isHeader
      header={<TypoGraphyKit variant="h6">پرداخت از درگاه بانکی</TypoGraphyKit>}
      disagreeText={"بستن"}
      agreeText={isPending ? "در حال پردازش..." : "ادامه"}
    >
      <Grid>
        <TypoGraphyKit variant="body1">
          شما در حال پرداخت قسط از طریق درگاه بانکی هستید.
        </TypoGraphyKit>
        <TypoGraphyKit variant="body1" sx={{ mt: 1 }}>
          مبلغ قابل پرداخت:{" "}
          <strong>{numberSeparator(paymentAmount)} ریال</strong>
        </TypoGraphyKit>
      </Grid>

      <StackKit
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        sx={{ mt: 3 }}
      >
        {chosePayment.map((item) => {
          const isActive = selectedGateway === item.title;
          return (
            <BoxKit
              key={item.title}
              sx={{
                width: "100px",
                height: "80px",
                bgcolor: "#fff",
                borderRadius: "10px",
                position: "relative",
                padding: "10px",
                cursor: "pointer",
                boxShadow: isActive ? "0 0 0 2px #1976d2" : "none",
              }}
              onClick={() => setSelectedGateway(item.title)}
            >
              {!isActive && (
                <div
                  style={{
                    background: "#7c7c7d",
                    opacity: 0.5,
                    borderRadius: "10px",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                  }}
                />
              )}
              <img
                src={item.imgUrl}
                alt={item.title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </BoxKit>
          );
        })}
      </StackKit>
    </AlertDialog>
  );
};

export default OnlinePayment;